import { ArrowDown, ArrowUp, Boxes, FileText, History, ListOrdered, Trash2 } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { TextField } from "../../web/components/fields";
import { RecordWindow } from "../../web/components/RecordWindow";
import { archiveAction, syncAction } from "../../web/components/recordActions";
import { SyncNotice } from "../../web/components/SyncAction";
import { SyncBadge } from "../../web/components/SyncBadge";
import { AddPicker } from "../../web/components/AddPicker";
import { type SyncOption, SyncSelect } from "../../web/components/SyncSelect";
import { Box, ErrorNotice, IconButton, Issues, Notice } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { useFreshRecord } from "../../web/lib/queries";
import { bundleTotals } from "./bundleTotals";
import { can, itemName, usePriceListSaver, whyNot } from "./data";
import { usePriceListWindows } from "./windows";
import { BUNDLE_MAX_ITEMS, type BundleLine, type BundleRow, ITEM_NUMBER_MAX, type PriceListData } from "./meta";
import { ChangedNotice, HistoryTab } from "./HistoryTab";
import { archivedInBundle, bundleNumberProblem, sharedNumberText } from "./attention";
import { itemNumberIssues } from "./setup";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";

/*
 * One bundle, in a window like an item's: Details (its name and item number) and Contents (the
 * items in it, how many of each, and what they add up to). A bundle synced with ServiceM8 can only
 * hold items that are in ServiceM8 too. Fields save as you leave them.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    { id: "contents", label: "Contents", icon: <ListOrdered className="size-5" /> },
    { id: "history", label: "History", icon: <History className="size-5" /> },
];

const itemNumberCheck = (required: boolean) => (v: string) => {
    if (v.length > ITEM_NUMBER_MAX) return `At most ${ITEM_NUMBER_MAX} characters`;
    return required && !v ? "ServiceM8 needs an item number for a bundle" : null;
};

/** Items a bundle can take: active ones, and (synced) only those in ServiceM8 too. */
function itemChoices(data: PriceListData, synced: boolean, except: readonly string[]): SyncOption[] {
    return data.materials
        .filter((m) => m.active && (!synced || m.synced) && !except.includes(m.uuid))
        .map((m) => ({ value: m.uuid, label: itemName(data, m), synced: m.synced, detail: m.item_number || undefined }));
}

export function BundleWindow(props: Readonly<{ data: PriceListData; uuid: string; tab: string; onTab: (tab: string) => void; onClose: () => void; onOpenItem: (uuid: string) => void }>) {
    const company = useCompany();
    const saver = usePriceListSaver();
    const b = props.data.bundles.find((x) => x.uuid === props.uuid);
    useFreshRecord(company.id, "materialbundle", b?.synced ? props.uuid : null);
    const [error, setError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup).
    const [duplicating, setDuplicating] = useState(false);
    const windows = usePriceListWindows();
    // The item number warning's button takes you to the field, every time it's pressed.
    const [numberFocus, setNumberFocus] = useState(0);
    const goToNumber = () => {
        props.onTab("details");
        setNumberFocus((n) => n + 1);
    };

    if (!b) {
        return (
            <RecordWindow kind="bundle" tone="slate" icon={<Boxes className="size-4" />} title="Bundle not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This bundle doesn't exist any more.</Notice>
            </RecordWindow>
        );
    }
    const canEdit = can(props.data, "bundles");
    const tab = TABS.some((t) => t.id === props.tab) ? props.tab : "details";
    const setActive = (active: boolean) => {
        setError(null);
        saver.updateBundle(b.uuid, { active }).catch((err: Error) => setError(err));
    };

    return (
        <RecordWindow
            kind="bundle"
            share
            tone="slate"
            icon={<Boxes className="size-4" />}
            title={
                <span className="inline-flex max-w-full items-center gap-2">
                    <SyncBadge synced={b.synced} onDark />
                    <span className="truncate">{b.name || "Untitled bundle"}</span>
                </span>
            }
            label={b.name || "Untitled bundle"}
            subtitle={b.item_number || undefined}
            tabs={TABS}
            tab={tab}
            onTab={props.onTab}
            onClose={props.onClose}
            actions={
                canEdit
                    ? [
                          syncAction("bundle", b.name || "Untitled bundle", b.synced, b.active, props.data.bundleSyncUnavailable, (on) => {
                              setError(null);
                              saver.setBundleSync(b.uuid, on).catch((err: Error) => setError(err));
                          }),
                          duplicateAction("bundle", () => setDuplicating(true)),
                          archiveAction("bundle", b.name || "Untitled bundle", b.active, setActive),
                      ]
                    : undefined
            }
            notices={
                <>
                    <ErrorNotice error={error} />
                    {/* Its problems as one box, worst first. */}
                    <Issues>
                        <BundleNumberNotice data={props.data} bundle={b} onDetails={canEdit ? goToNumber : undefined} />
                        <ArchivedItemsNotice data={props.data} bundle={b} onContents={tab === "contents" ? undefined : () => props.onTab("contents")} />
                        <ChangedNotice object="materialbundle" uuid={b.uuid} data={props.data} />
                    </Issues>
                    {b.active ? null : <Notice tone="warning">This bundle is archived: it's hidden in ServiceM8 and can't be added to jobs.</Notice>}
                    {b.active ? <SyncNotice kind="bundle" synced={b.synced} restoredInServiceM8={b.restoredInServiceM8} /> : null}
                    {whyNot(props.data, "bundles") ? <Notice>{whyNot(props.data, "bundles")}</Notice> : null}
                </>
            }
        >
            <div className="mx-auto w-full max-w-2xl space-y-4">
                {tab === "details" && (
                    <Box>
                        <TextField label="Name" value={b.name} disabled={!canEdit} validate={(v) => (v ? null : "Name is required")} onSave={(name) => saver.updateBundle(b.uuid, { name })} />
                        <TextField
                            label="Item number"
                            value={b.item_number}
                            disabled={!canEdit}
                            validate={itemNumberCheck(b.synced)}
                            hint="Unique across all items and bundles. Used to add it to jobs."
                            focusToken={numberFocus || undefined}
                            warning={bundleNumberProblem(b, itemNumberIssues([...props.data.materials, ...props.data.bundles]))}
                            onSave={(item_number) => saver.updateBundle(b.uuid, { item_number })}
                            className="max-w-sm"
                        />
                    </Box>
                )}
                {tab === "contents" && <Contents data={props.data} bundle={b} onOpenItem={props.onOpenItem} />}
                {tab === "history" && <HistoryTab object="materialbundle" uuid={b.uuid} data={props.data} />}
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="bundle"
                        name={b.name}
                        withNumber
                        supplierPrices={null}
                        // Its items and quantities, kept where it is (ServiceM8 or only here).
                        onCreate={(copy) =>
                            saver.createBundle({ name: copy.name, item_number: copy.itemNumber, material_list: b.material_list, synced: b.synced }).then((made) => windows.openBundle(made.uuid))
                        }
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </div>
        </RecordWindow>
    );
}

/** Its item number: none, or shared with an item or another bundle (accounting software matches invoice lines by it). */
function BundleNumberNotice({ data, bundle, onDetails }: Readonly<{ data: PriceListData; bundle: BundleRow; onDetails?: () => void }>) {
    const { missing, clashes } = itemNumberIssues([...data.materials, ...data.bundles]);
    const others = clashes.get(bundle.uuid);
    if (!bundle.active || (!missing.has(bundle.uuid) && !others)) return null;
    return (
        <Notice
            tone="warning"
            title={others ? "Shared item number." : "No item number."}
            actions={
                onDetails ? (
                    <button type="button" className="btn-secondary" onClick={onDetails}>
                        {others ? "Change item number" : "Add item number"}
                    </button>
                ) : null
            }
        >
            {others ? sharedNumberText(others, "bundle") : "Every bundle needs one."}
        </Notice>
    );
}

/** Items in it that are archived (or gone): they can't be added to jobs, so neither can it as it is. */
function ArchivedItemsNotice({ data, bundle, onContents }: Readonly<{ data: PriceListData; bundle: BundleRow; onContents?: () => void }>) {
    const archived = archivedInBundle(bundle, new Map(data.materials.map((m) => [m.uuid, { name: m.name, active: m.active }])));
    if (!bundle.active || !archived.length) return null;
    const one = archived.length === 1;
    return (
        <Notice
            tone="warning"
            title="Archived items in it."
            actions={
                onContents ? (
                    <button type="button" className="btn-secondary" onClick={onContents}>
                        See its contents
                    </button>
                ) : null
            }
        >
            It holds {one ? "an archived item" : `${archived.length} archived items`} ({archived.join(", ")}): archived items can&apos;t be added to jobs. Take{" "}
            {one ? "it" : "them"} out of the bundle, or restore {one ? "it" : "them"}.
        </Notice>
    );
}

function Contents({ data, bundle, onOpenItem }: Readonly<{ data: PriceListData; bundle: BundleRow; onOpenItem: (uuid: string) => void }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const byUuid = new Map(data.materials.map((m) => [m.uuid, m]));
    const lines = bundle.material_list;
    const totals = bundleTotals(data, lines);
    const canEdit = can(data, "bundles");
    const save = async (material_list: BundleLine[]) => {
        setError(null);
        try {
            return await saver.updateBundle(bundle.uuid, { material_list });
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };
    const move = (from: number, to: number) => {
        const next = [...lines];
        const [moved] = next.splice(from, 1);
        if (moved) next.splice(to, 0, moved);
        save(next).catch(() => undefined);
    };
    const profit = totals.priceNet - totals.costNet;

    return (
        <div className="space-y-4">
            <ErrorNotice error={error} />
            {lines.length === 0 ? <p className="text-sm text-muted">Nothing in it yet: add the items that go together.</p> : null}
            <ul className="divide-y divide-line rounded-md border border-line">
                {lines.map((l, i) => {
                    const m = byUuid.get(l.uuid);
                    return (
                        <li key={l.uuid} className="flex items-center gap-2 px-3 py-2 text-sm">
                            {m ? <SyncBadge synced={m.synced} /> : null}
                            <button type="button" className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline" disabled={!m} onClick={() => onOpenItem(l.uuid)}>
                                {m ? itemName(data, m) : "An item that no longer exists"}
                                {m && !m.active ? <span className="ml-2 text-xs text-muted">archived</span> : null}
                            </button>
                            <span className="w-24 shrink-0 text-right text-muted tabular-nums">{m ? fmt.money(m.price) : ""}</span>
                            <TextField
                                label="Quantity"
                                value={String(l.quantity)}
                                inputMode="decimal"
                                disabled={!canEdit}
                                validate={(v) => (Number(v) > 0 ? null : "Above 0")}
                                onSave={(v) => save(lines.map((x) => (x.uuid === l.uuid ? { ...x, quantity: Number(v) } : x)))}
                                className="w-24 shrink-0 [&_label]:sr-only"
                            />
                            {canEdit ? (
                                <span className="flex shrink-0 gap-1">
                                    <IconButton label="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}>
                                        <ArrowUp className="size-4" />
                                    </IconButton>
                                    <IconButton label="Move down" disabled={i === lines.length - 1} onClick={() => move(i, i + 1)}>
                                        <ArrowDown className="size-4" />
                                    </IconButton>
                                    <IconButton
                                        label="Remove"
                                        disabled={bundle.synced && lines.length === 1}
                                        onClick={() => save(lines.filter((x) => x.uuid !== l.uuid)).catch(() => undefined)}
                                    >
                                        <Trash2 className="size-4" />
                                    </IconButton>
                                </span>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
            {canEdit && lines.length < BUNDLE_MAX_ITEMS ? (
                // Adding one, as a group adds items: a search bar with an Add on each result (AddPicker).
                <div>
                    <AddPicker
                        placeholder="Add an item: search by name or item number"
                        options={itemChoices(
                            data,
                            bundle.synced,
                            lines.map((l) => l.uuid),
                        )}
                        onAdd={(uuid) => save([...lines, { uuid, quantity: 1 }])}
                        onUndo={(uuid) => save(lines.filter((l) => l.uuid !== uuid))}
                    />
                </div>
            ) : null}
            {bundle.synced ? <p className="text-xs text-muted">Only items in ServiceM8 can be in a bundle synced with it. A bundle holds 1 to {BUNDLE_MAX_ITEMS} items.</p> : null}

            <Box shaded className="space-y-1 text-sm" title="What it adds up to (each item at its own price)">
                <Total label="Price before tax" value={fmt.money(totals.priceNet)} strong />
                <Total label="Price with tax" value={fmt.money(totals.priceWithTax)} />
                {/* What the business pays and makes: only with See costs (the server leaves item costs out otherwise). */}
                {can(data, "costs.view") ? (
                    <>
                        <Total label="Cost before tax" value={fmt.money(totals.costNet)} />
                        <Total label="Made, before tax" value={totals.costNet > 0 ? `${fmt.money(profit)} (${fmt.percent(Math.round((profit / totals.costNet) * 100))} markup)` : fmt.money(profit)} />
                    </>
                ) : null}
                {totals.missing ? <p className="text-xs text-danger">{totals.missing} item(s) in it no longer exist and are left out.</p> : null}
            </Box>
        </div>
    );
}

function Total(props: Readonly<{ label: string; value: string; strong?: boolean }>) {
    return (
        <div className="flex justify-between gap-4">
            <span>{props.label}</span>
            <span className={props.strong ? "font-semibold tabular-nums" : "tabular-nums"}>{props.value}</span>
        </div>
    );
}

/** Adding a bundle: its name, item number and first item (ServiceM8 needs at least one). */
export function NewBundleWindow(props: Readonly<{ data: PriceListData; onCreated: (uuid: string) => void; onClose: () => void }>) {
    const saver = usePriceListSaver();
    const canSync = props.data.bundleSyncUnavailable === null;
    const [name, setName] = useState("");
    const [itemNumber, setItemNumber] = useState("");
    const [first, setFirst] = useState("");
    const [synced, setSynced] = useState(canSync);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const ready = name.trim() && (!synced || (itemNumber.trim() && first));
    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        saver
            .createBundle({ name: name.trim(), item_number: itemNumber.trim(), material_list: first ? [{ uuid: first, quantity: 1 }] : [], synced })
            .then((b) => props.onCreated(b.uuid))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <RecordWindow kind="bundle" tone="slate" icon={<Boxes className="size-4" />} title="New bundle" tabs={[{ id: "details", label: "Details", icon: <FileText className="size-5" /> }]} tab="details" onTab={() => undefined} onClose={props.onClose}>
            <form className="mx-auto w-full max-w-lg space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Name</span>
                    <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Item number</span>
                    <input className="input text-sm" value={itemNumber} maxLength={ITEM_NUMBER_MAX} onChange={(e) => setItemNumber(e.target.value)} required={synced} />
                </label>
                <div className="space-y-1">
                    <span className="text-xs font-medium text-muted">First item</span>
                    <SyncSelect label="First item" placeholder="Choose an item..." options={itemChoices(props.data, synced, [])} value={first} onChange={setFirst} />
                </div>
                {canSync ? (
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" className="mt-0.5 size-4" checked={synced} onChange={(e) => setSynced(e.target.checked)} />
                        <span>
                            <span className="block">Add to ServiceM8</span>
                            <span className="block text-xs text-muted">{synced ? "Synced with ServiceM8: it needs an item number and an item." : "Kept only in this app. You can add it to ServiceM8 later."}</span>
                        </span>
                    </label>
                ) : (
                    <Notice>{props.data.bundleSyncUnavailable}</Notice>
                )}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !ready}>
                        Add bundle
                    </button>
                </div>
            </form>
        </RecordWindow>
    );
}
